'use client';

import { create } from 'zustand';
/**
 * Background calls of the PWA layer. Unlike lib/api they never redirect to the login on a 401:
 * they also run on the sign-in pages and in the background, where a redirect would loop.
 */
async function call<T>(path: string, body?: Record<string, unknown>): Promise<T> {
  const res = await fetch(`/api${path}`, {
    method: body ? 'POST' : 'GET',
    credentials: 'same-origin',
    headers: { Accept: 'application/json', ...(body ? { 'Content-Type': 'application/json', 'x-invexa-csrf': '1' } : {}) },
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return (await res.json()) as T;
}
const get = <T,>(path: string) => call<T>(path);
const post = <T,>(path: string, body: Record<string, unknown> = {}) => call<T>(path, body);

// ---- Device detection ---------------------------------------------------------

export type Platform = 'ios' | 'android' | 'desktop';
export type Browser = 'safari' | 'chrome' | 'firefox' | 'edge' | 'samsung' | 'opera' | 'other';

export interface DeviceInfo {
  platform: Platform;
  browser: Browser;
  /** App with its own built-in browser (Instagram, Facebook, TikTok…): can't install from there. */
  inApp: string | null;
  /** iOS major version (push needs 16.4+ and the installed app). */
  iosVersion: number | null;
}

const IN_APP: [RegExp, string][] = [
  [/Instagram/i, 'Instagram'],
  [/FBAN|FBAV|FB_IAB|FBIOS/i, 'Facebook'],
  [/musical_ly|TikTok|BytedanceWebview/i, 'TikTok'],
  [/Line\//i, 'LINE'],
  [/Snapchat/i, 'Snapchat'],
  [/Twitter/i, 'X'],
  [/LinkedInApp/i, 'LinkedIn'],
  [/Telegram/i, 'Telegram'],
  [/WhatsApp/i, 'WhatsApp'],
  [/GSA\//i, 'Google'],
];

export function detectDevice(): DeviceInfo {
  if (typeof navigator === 'undefined') return { platform: 'desktop', browser: 'other', inApp: null, iosVersion: null };
  const ua = navigator.userAgent;
  // iPadOS 13+ reports itself as a Mac; touch support gives it away.
  const ios = /iPhone|iPad|iPod/i.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
  const android = /Android/i.test(ua);
  const platform: Platform = ios ? 'ios' : android ? 'android' : 'desktop';
  let browser: Browser = 'other';
  if (/SamsungBrowser/i.test(ua)) browser = 'samsung';
  else if (/EdgiOS|EdgA|Edg\//i.test(ua)) browser = 'edge';
  else if (/OPR\/|OPiOS|OPT\//i.test(ua)) browser = 'opera';
  else if (/FxiOS|Firefox/i.test(ua)) browser = 'firefox';
  else if (/CriOS|Chrome/i.test(ua)) browser = 'chrome';
  else if (/Safari/i.test(ua)) browser = 'safari';
  const inApp = IN_APP.find(([re]) => re.test(ua))?.[1] ?? (android && /; wv\)/.test(ua) ? 'outro app' : null);
  const iosVersion = ios ? Number(/OS (\d+)_/.exec(ua)?.[1] ?? /Version\/(\d+)/.exec(ua)?.[1] ?? 0) || null : null;
  return { platform, browser, inApp, iosVersion };
}

/** Running as the installed app (home-screen icon), not inside a browser tab. */
export function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    window.matchMedia('(display-mode: minimal-ui)').matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

// ---- Small persisted flags ----------------------------------------------------

function read(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}
function write(key: string, value: string) {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* private mode: the flag just isn't remembered */
  }
}

/** Remember "not now" for a while. */
export function snooze(key: string, days: number) {
  write(key, String(Date.now() + days * 86_400_000));
}
export function snoozed(key: string): boolean {
  return Number(read(key) ?? 0) > Date.now();
}

let memoryDeviceId: string | null = null;
/** Random id of this browser/app: ties its push subscription to its on-screen presence. */
export function deviceId(): string {
  const existing = read('invexa.deviceId') ?? memoryDeviceId;
  if (existing) return existing;
  const id = typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
  memoryDeviceId = id;
  write('invexa.deviceId', id);
  return id;
}

// ---- Store --------------------------------------------------------------------

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export type PushState = 'loading' | 'unsupported' | 'needs-install' | 'default' | 'denied' | 'off' | 'on';

interface PwaState {
  standalone: boolean;
  installEvent: BeforeInstallPromptEvent | null;
  guideOpen: boolean;
  /** A newer version is on the server; applied on the next navigation / return to the app. */
  updateReady: boolean;
  push: PushState;
  openGuide: () => void;
  closeGuide: () => void;
}

export const usePwa = create<PwaState>((set) => ({
  standalone: false,
  installEvent: null,
  guideOpen: false,
  updateReady: false,
  push: 'loading',
  openGuide: () => set({ guideOpen: true }),
  closeGuide: () => set({ guideOpen: false }),
}));

/** Install button: the browser's own prompt where it exists (Chrome/Edge/Samsung), else the step-by-step guide. */
export async function startInstall() {
  const ev = usePwa.getState().installEvent;
  if (!ev) return usePwa.getState().openGuide();
  // The event works once; Samsung Internet also throws when it was used by its own address-bar button.
  usePwa.setState({ installEvent: null });
  (window as Window & { __invexaInstall?: Event }).__invexaInstall = undefined;
  try {
    await ev.prompt();
    const choice = await ev.userChoice;
    if (choice.outcome !== 'accepted') usePwa.getState().openGuide();
  } catch {
    usePwa.getState().openGuide();
  }
}

// ---- Service worker + updates -------------------------------------------------

export const APP_VERSION = process.env.NEXT_PUBLIC_APP_VERSION ?? 'dev';
let registration: ServiceWorkerRegistration | null = null;

export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return null;
  try {
    registration = await navigator.serviceWorker.register('/sw.js', { scope: '/', updateViaCache: 'none' });
    return registration;
  } catch {
    return null;
  }
}

/** Ask the server which version it runs; flag an update when it differs from this page's code. */
export async function checkForUpdate(): Promise<boolean> {
  void registration?.update().catch(() => undefined);
  try {
    const res = await fetch('/app-version', { cache: 'no-store' });
    if (!res.ok) return false;
    const { version } = (await res.json()) as { version?: string };
    const newer = Boolean(version && version !== APP_VERSION);
    if (newer) usePwa.setState({ updateReady: true });
    return newer;
  } catch {
    return false;
  }
}

// ---- Push notifications -------------------------------------------------------

export function pushSupported(): boolean {
  return typeof window !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

function base64UrlToBytes(b64: string): Uint8Array<ArrayBuffer> {
  const pad = '='.repeat((4 - (b64.length % 4)) % 4);
  const raw = atob((b64 + pad).replace(/-/g, '+').replace(/_/g, '/'));
  const out = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

/** The worker once it is active: iOS refuses to subscribe while it is still installing. */
async function swRegistration(): Promise<ServiceWorkerRegistration | null> {
  if (!pushSupported()) return null;
  if (!registration) await registerServiceWorker();
  const ready = navigator.serviceWorker.ready.catch(() => null);
  const timeout = new Promise<null>((r) => setTimeout(() => r(null), 10_000));
  return (await Promise.race([ready, timeout])) ?? registration;
}

async function currentSubscription(): Promise<PushSubscription | null> {
  return (await (await swRegistration())?.pushManager.getSubscription().catch(() => null)) ?? null;
}

/**
 * Server's VAPID key, fetched ahead of the tap: Safari only lets a page subscribe right after
 * the user's gesture, so no network call may sit between the permission dialog and subscribe().
 */
let vapidKey: string | null = null;
async function loadVapidKey(): Promise<string> {
  if (vapidKey) return vapidKey;
  const { publicKey } = await get<{ publicKey: string }>('/notifications/push');
  vapidKey = publicKey;
  return publicKey;
}
export function preloadPushKey() {
  if (pushSupported()) void loadVapidKey().catch(() => undefined);
}

/** A subscription made with another server key (keys were regenerated) can't receive anything. */
function sameKey(sub: PushSubscription, key: string): boolean {
  const own = sub.options?.applicationServerKey;
  if (!own) return true;
  const a = new Uint8Array(own);
  const b = base64UrlToBytes(key);
  return a.length === b.length && a.every((v, i) => v === b[i]);
}

async function sendSubscription(sub: PushSubscription) {
  const json = sub.toJSON() as { endpoint: string; keys: { p256dh: string; auth: string } };
  await post('/notifications/push/subscribe', { endpoint: json.endpoint, keys: json.keys, deviceId: deviceId() });
}

/** Subscribe (or re-subscribe when the server key changed) and register the device with the server. */
async function subscribeDevice(reg: ServiceWorkerRegistration, key: string): Promise<PushSubscription> {
  let sub = await reg.pushManager.getSubscription();
  if (sub && !sameKey(sub, key)) {
    await sub.unsubscribe().catch(() => undefined);
    sub = null;
  }
  sub ??= await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: base64UrlToBytes(key) });
  await sendSubscription(sub);
  return sub;
}

/** Where notifications stand on this device (drives the prompt and Configurações). */
export async function refreshPushState(): Promise<PushState> {
  let state: PushState;
  const { platform } = detectDevice();
  if (!pushSupported()) state = platform === 'ios' && !isStandalone() ? 'needs-install' : 'unsupported';
  else if (Notification.permission === 'denied') state = 'denied';
  else if (Notification.permission === 'default') state = 'default';
  else {
    const sub = await currentSubscription();
    if (!sub) state = 'off';
    else {
      const { active } = await post<{ active: boolean }>('/notifications/push/status', { endpoint: sub.endpoint }).catch(() => ({ active: true }));
      state = active ? 'on' : 'off';
    }
  }
  usePwa.setState({ push: state });
  return state;
}

/** Set when the user turned notifications off here: app starts must not turn them back on. */
const PUSH_OFF_KEY = 'invexa.pushOff';

/** Why turning notifications on failed, in words the user can act on. */
export class PushError extends Error {}

/** Turn notifications on for this device. Call from a tap (iOS only asks for permission on a user gesture). */
export async function enablePush(): Promise<PushState> {
  if (!pushSupported()) return refreshPushState();
  // First thing in the tap: the permission dialog (Safari drops the gesture after any await).
  const permission = Notification.permission === 'granted' ? 'granted' : await Notification.requestPermission();
  if (permission !== 'granted') return refreshPushState();
  write(PUSH_OFF_KEY, '');
  const [reg, key] = await Promise.all([swRegistration(), loadVapidKey()]);
  if (!reg) throw new PushError('O app ainda está carregando. Feche e abra o Invexa e tente de novo.');
  try {
    await subscribeDevice(reg, key);
  } catch (err) {
    if (err instanceof Error && /HTTP/.test(err.message)) throw new PushError('Não conseguimos registrar este aparelho. Verifique a internet e tente de novo.');
    throw new PushError('O aparelho recusou a inscrição. Feche o app por completo, abra de novo e tente outra vez.');
  }
  realtimePresence?.();
  return refreshPushState();
}

/** Stop notifications on this device only (the account preferences stay). */
export async function disablePush(): Promise<PushState> {
  write(PUSH_OFF_KEY, '1');
  const sub = await currentSubscription();
  if (sub) {
    await post('/notifications/push/unsubscribe', { endpoint: sub.endpoint }).catch(() => undefined);
    await sub.unsubscribe().catch(() => undefined);
  }
  return refreshPushState();
}

/**
 * Every app start: if this device has permission, make sure the server has its current
 * endpoint (browsers rotate them; a reinstall makes a new one).
 */
export async function syncPush(): Promise<void> {
  if (!pushSupported() || Notification.permission !== 'granted' || read(PUSH_OFF_KEY) === '1') {
    await refreshPushState();
    return;
  }
  const reg = await swRegistration();
  const key = await loadVapidKey().catch(() => null);
  if (reg && key) await subscribeDevice(reg, key).catch(() => undefined);
  await refreshPushState();
}

export async function sendTestPush(): Promise<boolean> {
  const sub = await currentSubscription();
  if (!sub) return false;
  const { ok } = await post<{ ok: boolean }>('/notifications/push/test', { endpoint: sub.endpoint });
  return ok;
}

/** Set by the realtime client: re-announce presence right after subscribing. */
export let realtimePresence: (() => void) | null = null;
export function setRealtimePresence(fn: () => void) {
  realtimePresence = fn;
}

/** This device's id once it has push (presence only matters for devices that get notifications). */
export function pushDeviceId(): string | null {
  return read('invexa.deviceId') ?? memoryDeviceId;
}
